import { Category } from "../components/CategoryMegaMenu";

/**
 * Find the full path from root to the specified category
 * @param categories - Array of root categories with children
 * @param categoryId - The category ID to find
 * @returns Array of categories representing the path from root to target, or null if not found
 */
export function findCategoryPath(
  categories: Category[],
  categoryId: string
): Category[] | null {
  if (!categoryId || !categories || categories.length === 0) {
    return null;
  }

  const findPath = (
    cats: Category[],
    id: string,
    path: Category[] = []
  ): Category[] | null => {
    for (const cat of cats) {
      const currentPath = [...path, cat];
      
      // Found the target category
      if (String(cat.id) === String(id)) {
        return currentPath;
      }
      
      // Recursively search in children
      if (cat.children && cat.children.length > 0) {
        const found = findPath(cat.children, id, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  return findPath(categories, categoryId);
}

/**
 * Calculate the depth of a category in the hierarchy
 * @param categories - Array of root categories with children
 * @param categoryId - The category ID to calculate depth for
 * @returns Depth level (0 = root/Level-1, 1 = Level-2, 2 = Level-3, -1 if not found)
 */
export function calculateCategoryDepth(
  categories: Category[],
  categoryId: string
): number {
  const path = findCategoryPath(categories, categoryId);
  if (!path || path.length === 0) {
    return -1;
  }
  
  // Depth is 0-indexed: path length - 1
  // Level-1 (root) = depth 0, Level-2 = depth 1, Level-3 = depth 2
  return path.length - 1;
}

/**
 * Check if a category has a valid depth for product creation
 * Rules:
 * - Level-1 only (depth 0): NOT VALID
 * - Level-2 (depth 1): VALID only if it has no children
 * - Level-3 (depth 2): ALWAYS VALID
 * - Depth > 2: NOT VALID
 * @param categories - Array of root categories with children
 * @param categoryId - The category ID to validate
 * @returns true if category depth is valid for product creation
 */
export function isValidCategoryDepth(
  categories: Category[],
  categoryId: string
): boolean {
  if (!categoryId || !categories || categories.length === 0) {
    return false;
  }

  const path = findCategoryPath(categories, categoryId);
  if (!path || path.length === 0) {
    return false;
  }

  const depth = path.length - 1;

  // Level-1 only (depth 0) is NOT allowed
  if (depth === 0) {
    return false;
  }

  // Level-2 (depth 1) is valid only if it has no children
  if (depth === 1) {
    const level2Category = path[path.length - 1];
    // If Level-2 has children, must select Level-3
    if (level2Category.children && level2Category.children.length > 0) {
      return false; // Must select Level-3
    }
    return true; // Level-2 is valid if no children
  }

  // Level-3 (depth 2) is always valid
  if (depth === 2) {
    return true;
  }

  // Depth > 2 is not allowed
  return false;
}

/**
 * Get the display path string for a category (e.g., "Machinery > CNC Machines > Milling Machines")
 * @param categories - Array of root categories with children
 * @param categoryId - The category ID to get path for
 * @returns Display path string or empty string if not found
 */
export function getCategoryDisplayPath(
  categories: Category[],
  categoryId: string
): string {
  const path = findCategoryPath(categories, categoryId);
  if (!path || path.length === 0) {
    return "";
  }

  return path.map((cat) => cat.name).join(" > ");
}

/**
 * Check if a Level-2 category requires Level-3 selection
 * @param categories - Array of root categories with children
 * @param categoryId - The Level-2 category ID to check
 * @returns true if Level-3 selection is required
 */
export function requiresLevel3Selection(
  categories: Category[],
  categoryId: string
): boolean {
  const path = findCategoryPath(categories, categoryId);
  if (!path || path.length === 0) {
    return false;
  }

  const depth = path.length - 1;
  
  // Only Level-2 categories (depth 1) can require Level-3
  if (depth !== 1) {
    return false;
  }

  const level2Category = path[path.length - 1];
  return !!(level2Category.children && level2Category.children.length > 0);
}

/**
 * Search through all category levels (Level-1, Level-2, Level-3)
 * Returns Level-1 categories that match or have matching children
 * @param categories - Array of root categories with children
 * @param searchQuery - The search query string
 * @returns Filtered array of Level-1 categories that match the search
 */
export function searchCategories(
  categories: Category[],
  searchQuery: string
): Category[] {
  if (!searchQuery.trim()) {
    return categories;
  }

  const query = searchQuery.toLowerCase().trim();
  const matchingCategories: Category[] = [];
  const addedCategoryIds = new Set<string>();

  // Recursive function to search through all levels
  const searchInCategory = (category: Category): boolean => {
    // Check if current category name matches
    const nameMatches = category.name.toLowerCase().includes(query);
    
    // Check if any child matches
    let hasMatchingChild = false;
    if (category.children && category.children.length > 0) {
      for (const child of category.children) {
        if (searchInCategory(child)) {
          hasMatchingChild = true;
        }
      }
    }

    // If this category or any of its children match, return true
    return nameMatches || hasMatchingChild;
  };

  // For each Level-1 category, check if it or any of its children match
  for (const category of categories) {
    if (searchInCategory(category) && !addedCategoryIds.has(category.id)) {
      matchingCategories.push(category);
      addedCategoryIds.add(category.id);
    }
  }

  return matchingCategories;
}

type CategoryTreeNode = {
  children?: CategoryTreeNode[];
};

/**
 * Trim a category tree so navigation menus stay at Industry + Product (2 levels).
 * @param maxDepth - Number of levels to keep (2 = root + children only).
 */
export function limitCategoryTreeDepth<T extends CategoryTreeNode>(
  categories: T[],
  maxDepth: number,
  currentDepth = 0,
): T[] {
  if (!categories?.length || maxDepth <= 0) {
    return [];
  }

  return categories.map((category) => {
    const nextDepth = currentDepth + 1;
    if (nextDepth >= maxDepth) {
      return { ...category, children: [] };
    }

    const children = Array.isArray(category.children)
      ? limitCategoryTreeDepth(category.children, maxDepth, nextDepth)
      : [];

    return { ...category, children };
  });
}

